import { Component, OnInit, OnDestroy } from '@angular/core';
import { SessionStorage } from 'ngx-webstorage';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatSnackBar } from '@angular/material';
import { Observable } from 'rxjs/Observable';
import { DeletePopUpComponent } from '../../../common/delete-pop-up/delete-pop-up.component';
import { UploadHistoryComponent } from '../../../common/upload-history/upload-history.component';
import { AddEmployeeService } from './add-employee.service';
import { AddEmployee } from '../employee';
import { EmpDetails } from '../../admin.interface';
import 'rxjs/add/operator/takeWhile';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';

@Component({
  selector: 'app-add-employee',
  templateUrl: './add-employee.component.html',
  styleUrls: ['./add-employee.component.css']
})
export class AddEmployeeComponent implements OnInit, OnDestroy {
  @SessionStorage() public user;
  @SessionStorage() public config;
  public employeeDetails: EmpDetails = new EmpDetails('');
  public employeeEditDetails: EmpDetails = new EmpDetails('');
  public employeeList: EmpDetails[] = [];
  public submitted: boolean = false;
  public submittedEdit: boolean = false;
  public filteredOptions: Observable<string[]>;
  public rmListarray: any = [];
  private alive: boolean = true;
  private isValidReportingManager: boolean = false;
  public employeeDetail: any;

  constructor(
    private addEmployeeService: AddEmployeeService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private spinnerService: Ng4LoadingSpinnerService
  ) { }

  ngOnInit() {
    this.getRmList();
  }

  /**
   * @function getRmList()
   * @description
   * Function to get the list of the RM in the employee list and show the particular Rm in the autocomplete Module
   * @var rmListarray An array of object to store the list of the RM obtained from the API
   * @author gaurav.rao
   */
  getRmList() {
    this.addEmployeeService.getrmList().takeWhile(() => this.alive).subscribe(response => {
      response.payload.forEach(value => this.rmListarray.push(value));
    },
      (error) => {
        this.snackBar.open('Error retriving the RM list', '', {
          duration: 3000
        });
      });
  }
  /**
   * @function reportingManagerSelected()
   * @description
   * Function to get the Employee Id of the RM selected from the auto complete dropdown list
   * @var employeeDetails An object which holds the Value of the reportingManagerId selected by the Admin
   * @param selectedoption An object of the selected RM from the dropdown list
   * @author gaurav.rao
   */
  reportingManagerSelected(selectedoption) {
    this.employeeDetails.reportingManagerId = selectedoption.Empno;
  }

  /**
   * @function changeReportManager()
   * @description
   * Function to get the Employee Id of the RM selected form the auto complete dropdown list
   * This function is called within the edit popup of the page view
   * @param selectedOption An object of the selected RM from the dropdown list
   * @author gaurav.rao
   */
  changeReportManager(selectedoption) {
    this.employeeEditDetails.reportingManagerId = selectedoption.Empno;
  }
  /**
   * @function filterEntities()
   * @description
   * A function to filter out the RM list by the input/ search item inputted by the Admin in RM input field in the view
   * The following is for the auto complete feature to work as per the key input provided
   * @param event $event object upon key down in RM auto complete input
   * @var searchItem A varable to store the search value entered by the Admin in the respectiove input box
   * @var filteredOptions A variable to store the Filtered RM list for the view purpose
   * @author gaurav.rao
   */
  filterEntities(event: any) {
    const searchItem = event.target.value;
    if (event.target.value !== 'undefined') {
      this.filteredOptions = this.rmListarray.filter(s => s.Empname.toLowerCase().indexOf(searchItem.toLowerCase()) == 0);
    }
  }

  /**
   * @function checkValidManager()
   * @description
   *A  function to check valid reporting manager
   * @param on blur and form submit checks for valid reporting manager
   * @var reportingManager param has name of the entered manager
   * @author Abhiraj SN
   */
  checkValidManager(reportingManager) {
    if (reportingManager) {
      const selectedRM = this.rmListarray.filter(item => item.Empname === reportingManager);
      if (selectedRM.length === 0) {
        this.snackBar.open(this.newMethod(), '', {
          duration: 3000
        });
        return false;
      } else {
        return true;
      }
    }
  }

  private newMethod(): string {
    return 'Please select valid Reporting Manager';
  }

  /**
   * @function deleteEmployee
   * @description
   * A function to delete the employee available in the organisation by the click event upon valid selection from the table view
   * @param index The index value of the selected employee to delete from the list provided for view puprose
   * @var employeeList An array of object which holds the employee details entered by the admin
   * @requires DeletePopupComponent An confirm modal before delete the Employee from the lsit before actually adding it to the view
   * @author gaurav.rao
   */
  deleteEmployee(index) {
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(DeletePopUpComponent);
      dialogRef.afterClosed().subscribe(result => {
        if (result == true) {
          this.employeeList.splice(index, 1);
        }
      });
    }
  }

  /**
   * @function editEmployee
   * @description
   * The function to enable the edit popup to show up by closing down all the popup
   * The particular object of the employee will be updated to the popup data in order to edit
   * @param i The index of the employee selected to edit from the temporary table view
   * @var employeeEditDetails An variable to store the selected employee detail for edit purpose
   * @var editMode An flag to show/hide the edit popup
   * @author gaurav.rao
   */
  editEmployee(i) {
    if (this.employeeList) {
      // Loop and close all the popup except the clicked
      this.employeeList.filter((ele, j) => j != i).map((element) => element.editMode = false);
    }
    this.employeeList[i].editMode = true;
    this.employeeEditDetails = this.employeeList[i];
  }

  /**
   * @function saveEmployee()
   * @description
   * The function to submit the employee available in the temporary view table to backend
   * Multiple entries are looped and passed as different API calls
   * @argument employeeList An Array of Employee list which needs to be added to the system
   * @argument length Total length of the Employee detail available for adding
   * @author gaurav.rao
  */
  saveEmployee() {
    const length = this.employeeList.length;
    this.employeeList.forEach((element, i) => {
      const data = new AddEmployee(element);
      data.userObj = this.user; // Add User object for mapping
      this.addEmployeeService.saveEmployee(data).takeWhile(() => this.alive).subscribe((response) => {
        if (i === length - 1) { // Show popup only once, ie. for last element
          if (length === 1) { // Show single employee is added
            this.snackBar.open('Employee Added Successfully', '', {
              duration: 3000
            });
          } else if (length > 1) { // Multi Employee is added
            this.snackBar.open('Employees Added Successfully', '', {
              duration: 3000
            });
          }
          this.backToEmployee();
        }
      },
        error => {
          this.snackBar.open('Error while adding the employee', '', {
            duration: 3000
          });
        });
    });
  }

  /**
   * @function readCsvFile()
   * @description
   * Function to upload the CSV file for employee addition
   * @param event$ An file event having actual file content for sending it to the BE for Upload
   * @author gaurav.rao
   */
  readCsvFile(event$: any) {
    this.spinnerService.show();
    const file = event$.target.files[0];
    let formData: FormData = new FormData();
    formData.append('file', file, file.name);
    this.addEmployeeService.uploadCSV(formData).takeWhile(() => this.alive).subscribe(response => {
      this.spinnerService.hide();
      this.snackBar.open('CSV uploaded successfully', '', {
        duration: 3000
      });
    }, (error) => {
      this.spinnerService.hide();
      this.snackBar.open('Incorrect file format', '', {
        duration: 3000
      });
    });
  }

  /**
   * @function readIpdoCsvFile()
   * @description
   * Function to upload the CSV file for ipdo employee addition
   * @param event$ An file event having actual file content for sending it to the BE for Upload
   * @author abhiraj.sn
   */
  readIpdoCsvFile(event$: any) {
    this.spinnerService.show();
    const file = event$.target.files[0];
    let formData: FormData = new FormData();
    formData.append('file', file, file.name);
    this.addEmployeeService.uploadIpdoCSV(formData).takeWhile(() => this.alive).subscribe(response => {
      this.spinnerService.hide();
      this.snackBar.open('CSV uploaded successfully', '', {
        duration: 3000
      });
    }, (error) => {
      this.spinnerService.hide();
      this.snackBar.open('Incorrect file format', '', {
        duration: 3000
      });
    });
  }

  /* Navigate Back to Employee View Page */
  backToEmployee() {
    this.router.navigate(['/admin/employees']);
  }

  /**
   * @function onSubmit()
   * @description
   * Function to add the single employee list to the Table view below iff Valid
   * @param valid Boolean to check if all the requirement is satisfied
   * @argument employeeList An variable to store the new employee added for API call and table view
   * @author gaurav,rao
   */
  onSubmit(valid) {
    this.submitted = true;
    if (!!valid && !!this.employeeDetails.reportingManagerName && this.config.businessUnit === 'accenture') {
      valid = this.checkValidManager(this.employeeDetails.reportingManagerName);
    }
    if (valid) {
      this.employeeList.push(this.employeeDetails); // Push to the Array for mtable view
      this.employeeDetails = new EmpDetails(''); // Clear the Single Add employee form after the push
      this.submitted = false;
    }
  }

  /**
   * @function update()
   * @description
   * Function to Update the employee value in edit popup view upon click of Update
   * @param form Form Element to check the Validation of the update form
   * @param i Index value of the choosen employee from the table view
   * @argument employeeEditDetails An variable which holds the update form values
   * @argument employeeList An variable to store the new employee added for API call and table view
   * @author gaurav.rao
   */
  update(form, i) {
    this.submittedEdit = true;
    if (!!form.valid && !!this.employeeEditDetails.reportingManagerName && this.config.businessUnit === 'accenture') {
      this.isValidReportingManager = this.checkValidManager(this.employeeEditDetails.reportingManagerName);
    }
    if (this.isValidReportingManager && this.config.businessUnit === 'accenture') {
      this.employeeList[i] = this.employeeEditDetails;
      if (this.employeeList[i].reportingManagerName == '') {
        this.employeeList[i].reportingManagerId = null;
      }
      this.employeeList[i].editMode = false;
    } else if (form.valid) {
      this.employeeList[i] = this.employeeEditDetails;
      if (this.employeeList[i].reportingManagerName == '') {
        this.employeeList[i].reportingManagerId = null;
      }
      this.employeeList[i].editMode = false;
    }
    this.isValidReportingManager = false;
  }

  /* Close the edit popup */
  closeAll(i) {
    this.employeeList[i].editMode = false;
  }

  /**
   * @function fileUploadHistory()
   * @description
   * Function to Get the upload csv history and view the same in Dialog box
   * @requires UploadHistoryComponent An data view modal for the Upload history Dialog
   * @author gaurav.rao
  */
  fileUploadHistory(businessUnit) {
    this.addEmployeeService.uploadHistory(businessUnit).takeWhile(() => this.alive).subscribe(response => {
      if (this.dialog.openDialogs.length === 0) {
        const dialogRef = this.dialog.open(UploadHistoryComponent, {
          width: '50%',
          data: response.payload
        });
      }
    },
      (error) => {
        this.snackBar.open('Error retriving the upload history', '', {
          duration: 3000
        });
      });
  }

  /**
   * @function ngOnDestroy()
   * @description
   * Function to destroy the Subscription used while calling API
   * Unsubscribe the observable is done using takeUntil method of rxjs
   * @author gaurav.rao
  */
  ngOnDestroy() {
    this.alive = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/employees/add-employee/add-employee.component.ts